import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Config from '../utils/Config';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLocationArrow, faSearch, faSpinner, faEye, faCheckCircle,
  faAngleDoubleLeft, faAngleLeft, faAngleRight, faAngleDoubleRight
} from "@fortawesome/free-solid-svg-icons";
import "./DealerList.css";

const PREF_KEY = "ffl:listPrefs:v1";
const DEFAULT_PREFS = { itemsPerPage: 12, sortBy: "" };

const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return "N/A";
  const cleaned = ("" + phoneNumber).replace(/\D/g, "");
  if (cleaned.length === 10) {
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    return match ? `(${match[1]}) ${match[2]}-${match[3]}` : phoneNumber;
  }
  return phoneNumber;
};

const telHref = (raw, country = '+1') => {
  const digits = (raw || '').replace(/\D/g, '');
  const intl = digits.length === 10 ? country + digits : (digits.startsWith('+') ? digits : '+' + digits);
  return `tel:${intl}`;
};

function loadPrefs() {
  try {
    const raw = localStorage.getItem(PREF_KEY);
    if (raw) return { ...DEFAULT_PREFS, ...JSON.parse(raw) };
    // migrate legacy keys once
    const ip = Number(localStorage.getItem("itemsPerPage"));
    const sb = localStorage.getItem("sortBy");
    const migrated = {
      ...DEFAULT_PREFS,
      ...(Number.isFinite(ip) && [6, 12, 24].includes(ip) ? { itemsPerPage: ip } : {}),
      ...(sb ? { sortBy: sb } : {})
    };
    localStorage.setItem(PREF_KEY, JSON.stringify(migrated));
    localStorage.removeItem("itemsPerPage");
    localStorage.removeItem("sortBy");
    return migrated;
  } catch {
    return DEFAULT_PREFS;
  }
}

function savePrefs(next) {
  localStorage.setItem(PREF_KEY, JSON.stringify(next));
}

function applySort(arr, criteria) {
  const out = [...arr];
  switch (criteria) {
    case "Business Name":
      out.sort((a, b) => (a.BUSINESS_NAME || "").localeCompare(b.BUSINESS_NAME || ""));
      break;
    case "License Name":
      out.sort((a, b) => (a.LICENSE_NAME || "").localeCompare(b.LICENSE_NAME || ""));
      break;
    case "License Type":
      out.sort((a, b) => (a.LIC_TYPE || "").localeCompare(b.LIC_TYPE || ""));
      break;
    case "Zipcode":
      out.sort((a, b) => (a.PREMISE_ADDRESS?.ZIP_CODE || "").localeCompare(b.PREMISE_ADDRESS?.ZIP_CODE || ""));
      break;
    case "City":
      out.sort((a, b) => (a.PREMISE_ADDRESS?.CITY || "").localeCompare(b.PREMISE_ADDRESS?.CITY || ""));
      break;
    case "State":
      out.sort((a, b) => (a.PREMISE_ADDRESS?.STATE || "").localeCompare(b.PREMISE_ADDRESS?.STATE || ""));
      break;
    case "Phone Number":
      out.sort((a, b) => (a.VOICE_PHONE || "").localeCompare(b.VOICE_PHONE || ""));
      break;
    case "Expiration Date":
      out.sort((a, b) => (a.LIC_XPRDTE || "").localeCompare(b.LIC_XPRDTE || ""));
      break;
    case "Distance":
      out.sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity));
      break;
    default:
      // no sort or unrecognized: leave original order
      break;
  }
  return out;
}

const DealerList = ({
  dealers,
  loading,
  searchBarType = "myFFLSearchBar",
  showDistanceSort = false,
  currentPage,
  setCurrentPage
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  // hydrate prefs once
  const [prefs, setPrefs] = useState(loadPrefs());
  const [sortCriteria, setSortCriteria] = useState(prefs.sortBy || "");
  const [itemsPerPage, setItemsPerPage] = useState(prefs.itemsPerPage || 12);

  const [list, setList] = useState(applySort(dealers, prefs.sortBy));

  // keep local list synced and (re)sorted whenever dealers or sort changes
  useEffect(() => {
    setList(applySort(dealers, sortCriteria));
    setCurrentPage(1);
  }, [dealers, sortCriteria, setCurrentPage]);

  // keep prefs saved whenever either value changes
  useEffect(() => {
    const next = { ...prefs, itemsPerPage, sortBy: sortCriteria };
    setPrefs(next);
    savePrefs(next);
  }, [itemsPerPage, sortCriteria]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPages = Math.max(1, Math.ceil(list.length / (itemsPerPage || 1)));

  // clamp current page if data shrinks
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [currentPage]);

  const handleCardClick = (dealerId) => {
    navigate(`/dealer/${dealerId}`, {
      state: { origin: `${location.pathname}${location.search}` }
    });
  };

  const handleWebsiteClick = (verified, subdomain) => {
    if (verified) {
      const dealerURL = Config.API.WEBSITE_BASE_URL.replace("<sitename>", subdomain);
      window.open(dealerURL, "_blank", "noopener,noreferrer");
    }
  };

  const handleSortChange = (e) => {
    const criteria = e.target.value;
    setSortCriteria(criteria);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleItemsPerPageChange = (e) => {
    const n = Number(e.target.value);
    setItemsPerPage(n);
    setCurrentPage(1);
  };

  const indexOfLastDealer = currentPage * itemsPerPage;
  const indexOfFirstDealer = indexOfLastDealer - itemsPerPage;
  const currentDealers = list.slice(indexOfFirstDealer, indexOfLastDealer);

  if (loading) {
    return (
      <div className="loading-icon">
        <FontAwesomeIcon icon={faSpinner} spin size="3x" />
        <p>Loading records...</p>
      </div>
    );
  }

  if (!list.length) {
    return (
      <div className="explanation">
        <h2 className="search-title">
          Find <span className="hide-on-mobile">a Licensed </span>
          FFL Dealer by {searchBarType === "searchBar" ? "Name" : "Location"}
        </h2>
        <p>
          Federal Firearms License (FFL) holders are authorized to legally sell, transfer, or handle firearms and ammunition. Dealers must meet strict federal regulations, so you can trust they’re qualified.
        </p>
        {searchBarType === "searchBar" ? (
          <>
            <p>
              Use the search bar above to find licensed dealers near you. You can search by Business Name, License Name, Street, City, State, Phone, or ZIP Code.
            </p>
            <span className="search-by-text"><FontAwesomeIcon icon={faLocationArrow} /></span>
            <p
              className={`search-by-text ${searchBarType === "searchBar" ? "active" : ""}`}
              onClick={() => navigate("/")}
            >
              Search by Location
            </p>
          </>
        ) : (
          <>
            <p>
              Use the search above to enter your address and choose a distance — we’ll show you licensed FFL dealers nearby.
            </p>
            <span className="search-by-text"><FontAwesomeIcon icon={faSearch} /></span>
            <p
              className={`search-by-text ${searchBarType === "searchBar" ? "active" : ""}`}
              onClick={() => navigate("/name-search")}
            >
              Search by Name
            </p>
          </>
        )}
      </div>
    );
  }

  const renderPagination = (position = "bottom") => (
    <div className={`pagination-container ${position === "top" ? "pagination-top" : ""}`}>
      <div className="pagination">
        <button onClick={() => handlePageChange(1)} disabled={currentPage === 1}>
          <FontAwesomeIcon icon={faAngleDoubleLeft} /> First
        </button>
        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
          <FontAwesomeIcon icon={faAngleLeft} /> Previous
        </button>

        <div className="desktop-itemsperpage">
          <span className="page-number">Page {currentPage} of {totalPages}</span>
          <select value={itemsPerPage} onChange={handleItemsPerPageChange}>
            <option value={6}>6</option>
            <option value={12}>12</option>
            <option value={24}>24</option>
          </select>
        </div>

        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
          <FontAwesomeIcon icon={faAngleRight} /> Next <FontAwesomeIcon icon={faAngleRight} />
        </button>
        <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}>
          <FontAwesomeIcon icon={faAngleDoubleRight} /> Last
        </button>

        <div className="mobile-itemsperpage">
          <span className="page-number">Page {currentPage} of {totalPages}</span>
          <select value={itemsPerPage} onChange={handleItemsPerPageChange}>
            <option value={6}>6</option>
            <option value={12}>12</option>
            <option value={24}>24</option>
          </select>
        </div>
      </div>
    </div>
  );

  return (
    <div className="dealer-list-container">

      {itemsPerPage >= 24 && list.length > 24 && renderPagination("top")}

      <div className="record-sort-container">
        <div className="record-count">
          {`${list.length} record${list.length > 1 ? "s" : ""} found`}
        </div>
        <div className="sort-dropdown">
          <select id="sortCriteria" value={sortCriteria} onChange={handleSortChange}>
            <option value="">Sort By</option>
            <option value="Business Name">Business Name</option>
            <option value="License Name">License Name</option>
            <option value="License Type">License Type</option>
            <option value="Zipcode">Zipcode</option>
            <option value="City">City</option>
            <option value="State">State</option>
            <option value="Phone Number">Phone Number</option>
            <option value="Expiration Date">Expiration Date</option>
            {showDistanceSort && <option value="Distance">Distance (Shortest First)</option>}
          </select>
        </div>
      </div>

      <div className="dealer-list">
        {currentDealers.map((dealer) => (
          <div
            key={dealer._id}
            className={`dealer-card ${dealer.VERIFIED ? "verified" : ""}`}
            onClick={() => handleCardClick(dealer._id)}
            style={{ cursor: "pointer" }}
          >
            <div className="details-top-right">
              {dealer.VERIFIED && (
                <a
                  className="sdv-btn website"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Visit website for ${dealer.BUSINESS_NAME || dealer.LICENSE_NAME || "dealer"}`}
                  title="Visit website"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleWebsiteClick(dealer.VERIFIED, dealer.SUBDOMAIN);
                  }}
                >
                  🌐
                </a>
              )}
            </div>
            <h3>
              {dealer.BUSINESS_NAME || dealer.LICENSE_NAME || "N/A"}
              {dealer.VERIFIED && (
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  title="Verified dealer"
                  aria-label="Verified dealer"
                  style={{ marginLeft: 8, color: "#22c55e" }}
                />
              )}
            </h3>
            {/* <p><strong>License Name:</strong> {dealer.LICENSE_NAME || "N/A"}</p> */}
            <p className="dealerAddress">
              {dealer.PREMISE_ADDRESS
                ? `${dealer.PREMISE_ADDRESS.STREET || "N/A"}`
                : "N/A"}<br />
              {dealer.PREMISE_ADDRESS
                ? `${dealer.PREMISE_ADDRESS.CITY || "N/A"}, ${dealer.PREMISE_ADDRESS.STATE || "N/A"} ${dealer.PREMISE_ADDRESS.ZIP_CODE || "N/A"}`
                : "N/A"}
            </p>
            <p>
              ☎️&nbsp;&nbsp;
              <a
                className="phone-link"
                href={telHref(dealer.VOICE_PHONE)}
                aria-label={`Call ${dealer.VOICE_PHONE}`}
                onClick={(e) => e.stopPropagation()}
              >
                {formatPhoneNumber(dealer.VOICE_PHONE)}
              </a>
            </p>
            {showDistanceSort && (
              <p>
                <strong>Distance:</strong>{" "}
                {dealer.distance ? `${dealer.distance.toFixed(1)} miles` : "N/A"}
              </p>
            )}
          </div>
        ))}
      </div>

      <div className="pagination-container">
        <div className="pagination">
          <button onClick={() => handlePageChange(1)} disabled={currentPage === 1}>
            <FontAwesomeIcon icon={faAngleDoubleLeft} /> First
          </button>
          <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
            <FontAwesomeIcon icon={faAngleLeft} /> Previous
          </button>

          <div className="desktop-itemsperpage">
            <span className="page-number">Page {currentPage} of {totalPages}</span>
            <select value={itemsPerPage} onChange={handleItemsPerPageChange}>
              <option value={6}>6</option>
              <option value={12}>12</option>
              <option value={24}>24</option>
            </select>
          </div>

          <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
            <FontAwesomeIcon icon={faAngleRight} /> Next <FontAwesomeIcon icon={faAngleRight} />
          </button>
          <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}>
            <FontAwesomeIcon icon={faAngleDoubleRight} /> Last
          </button>

          <div className="mobile-itemsperpage">
            <span className="page-number">Page {currentPage} of {totalPages}</span>
            <select value={itemsPerPage} onChange={handleItemsPerPageChange}>
              <option value={6}>6</option>
              <option value={12}>12</option>
              <option value={24}>24</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DealerList;
